import React, { useState, useEffect } from 'react'
import { Menu, MenuButton, MenuItems, Transition, Dialog, DialogPanel, DialogTitle, TransitionChild } from '@headlessui/react'
import { DocumentPlusIcon, ChevronDownIcon, ExclamationTriangleIcon, CheckCircleIcon, PaperAirplaneIcon } from '@heroicons/react/20/solid'

export default function GenerateInvoicesModal({
  previewUrl,
  generateUrl,
  sendRemindersUrl,
  csrfToken,
  defaultDueDate,
  fundName,
  // If we just generated invoices, show the success state
  justGenerated = false,
  generatedCount = 0,
  generatedDueDate = null
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState(justGenerated ? 'success' : 'form') // 'form', 'preview', 'loading', 'success'
  const [dueDate, setDueDate] = useState(defaultDueDate || '')
  const [skipExisting, setSkipExisting] = useState(true)
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(justGenerated ? { invoices_created: generatedCount, due_date: generatedDueDate } : null)
  const [sendingReminders, setSendingReminders] = useState(false)

  // Open modal automatically if we just generated invoices
  useEffect(() => {
    if (justGenerated && generatedCount > 0) {
      setIsOpen(true)
    }
  }, [justGenerated, generatedCount])

  const fetchPreview = async () => {
    setError(null)
    setStep('loading')

    try {
      const url = new URL(previewUrl, window.location.origin)
      url.searchParams.append('due_date', dueDate)
      url.searchParams.append('skip_existing', skipExisting)

      const response = await fetch(url.toString(), {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Failed to fetch preview')

      const data = await response.json()
      if (data.success) {
        setPreview(data)
        setStep('preview')
      } else {
        setError(data.error || 'Failed to generate preview')
        setStep('form')
      }
    } catch (err) {
      setError('Failed to fetch preview. Please try again.')
      setStep('form')
    }
  }

  const handleGenerate = async () => {
    setError(null)
    setStep('loading')

    try {
      const formData = new FormData()
      formData.append('due_date', dueDate)
      formData.append('skip_existing', skipExisting)
      formData.append('authenticity_token', csrfToken)

      const response = await fetch(generateUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to generate invoices')

      const data = await response.json()
      if (data.success) {
        setResult(data)
        setStep('success')
      } else {
        setError(data.error || 'Failed to generate invoices')
        setStep('preview')
      }
    } catch (err) {
      setError('Failed to generate invoices. Please try again.')
      setStep('preview')
    }
  }

  const handleSendReminders = async () => {
    setSendingReminders(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('due_date', result.due_date)
      formData.append('authenticity_token', csrfToken)

      const response = await fetch(sendRemindersUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        window.location.reload()
      } else {
        setError('Failed to send reminders. Please try again.')
        setSendingReminders(false)
      }
    } catch (err) {
      setError('Failed to send reminders. Please try again.')
      setSendingReminders(false)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    // Reset state after modal animation
    setTimeout(() => {
      if (!justGenerated) {
        setStep('form')
        setPreview(null)
        setResult(null)
        setError(null)
      }
    }, 300)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
      >
        <DocumentPlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
        Generate Invoices
      </button>

      {/* Modal */}
      <Transition show={isOpen}>
        <Dialog className="relative z-50" onClose={handleClose}>
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">

                  {/* Form Step */}
                  {step === 'form' && (
                    <>
                      <div>
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                          <DocumentPlusIcon className="h-6 w-6 text-emerald-600" aria-hidden="true" />
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                          <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                            Generate Invoices Manually
                          </DialogTitle>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">
                              Create invoices for all active members in <strong>{fundName}</strong> for a specific due date.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="mt-4 rounded-md bg-blue-50 p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">When to use this feature</h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p>Use this when auto-invoicing wasn't enabled in time, or when you need to create invoices for a specific period that was missed.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {error && (
                        <div className="mt-4 rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-5 space-y-4">
                        <div>
                          <label htmlFor="due_date" className="block text-sm font-medium text-gray-700">
                            Invoice Due Date
                          </label>
                          <input
                            type="date"
                            id="due_date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Select the date these invoices should be due
                          </p>
                        </div>

                        <div className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id="skip_existing"
                              type="checkbox"
                              checked={skipExisting}
                              onChange={(e) => setSkipExisting(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label htmlFor="skip_existing" className="font-medium text-gray-900">
                              Skip members with existing invoices
                            </label>
                            <p className="text-gray-500">
                              Members who already have an invoice for this due date will not receive a new one
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          onClick={fetchPreview}
                          disabled={!dueDate}
                          className="inline-flex w-full justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed sm:col-start-2"
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={handleClose}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}

                  {/* Loading Step */}
                  {step === 'loading' && (
                    <div className="py-12 text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-emerald-600 border-r-transparent"></div>
                      <p className="mt-4 text-sm text-gray-500">Processing...</p>
                    </div>
                  )}

                  {/* Preview Step */}
                  {step === 'preview' && preview && (
                    <>
                      <div>
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                          <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                            Confirm Invoice Generation
                          </DialogTitle>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">
                              Review the details below before generating invoices.
                            </p>
                          </div>
                        </div>
                      </div>

                      {error && (
                        <div className="mt-4 rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-5">
                        <dl className="divide-y divide-gray-200">
                          <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                              {formatDate(preview.due_date)}
                            </dd>
                          </div>
                          <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Active Members</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                              {preview.total_active_members}
                            </dd>
                          </div>
                          {preview.has_existing_invoices && (
                            <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Existing Invoices</dt>
                              <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                                <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                                  {preview.existing_invoices_count} members already have invoices for this date
                                </span>
                              </dd>
                            </div>
                          )}
                          {preview.members_with_zero_dues > 0 && (
                            <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Zero Dues</dt>
                              <dd className="mt-1 text-sm text-gray-500 sm:col-span-2 sm:mt-0">
                                {preview.members_with_zero_dues} members have $0 dues (will be skipped)
                              </dd>
                            </div>
                          )}
                          <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-900">Invoices to Create</dt>
                            <dd className="mt-1 text-sm font-semibold text-emerald-600 sm:col-span-2 sm:mt-0">
                              {preview.invoices_to_create}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {preview.invoices_to_create === 0 && (
                        <div className="mt-4 rounded-md bg-yellow-50 p-4">
                          <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                            <div className="ml-3">
                              <p className="text-sm text-yellow-700">
                                No new invoices will be created. All active members either already have an invoice for this date or have $0 dues.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        <button
                          type="button"
                          onClick={handleGenerate}
                          disabled={preview.invoices_to_create === 0}
                          className="inline-flex w-full justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed sm:col-start-2"
                        >
                          Generate {preview.invoices_to_create} Invoices
                        </button>
                        <button
                          type="button"
                          onClick={() => { setStep('form'); setPreview(null); }}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        >
                          Back
                        </button>
                      </div>
                    </>
                  )}

                  {/* Success Step */}
                  {step === 'success' && result && (
                    <>
                      <div>
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                          <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                          <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                            Invoices Generated Successfully!
                          </DialogTitle>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">
                              Created <strong>{result.invoices_created}</strong> invoices due on{' '}
                              <strong>{formatDate(result.due_date)}</strong>.
                            </p>
                          </div>
                        </div>
                      </div>

                      {error && (
                        <div className="mt-4 rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-5 rounded-md bg-blue-50 p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <PaperAirplaneIcon className="h-5 w-5 text-blue-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">Send Reminders?</h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p>Would you like to send invoice reminder emails to members for these newly created invoices?</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 sm:mt-6 space-y-3">
                        <button
                          type="button"
                          onClick={handleSendReminders}
                          disabled={sendingReminders}
                          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          <PaperAirplaneIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                          {sendingReminders ? 'Sending...' : 'Send Reminder Emails'}
                        </button>
                        <button
                          type="button"
                          onClick={() => window.location.reload()}
                          className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          Done (Don't Send Reminders)
                        </button>
                      </div>
                    </>
                  )}

                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

